<template>
  <ChatContactFormSubmit v-if="showChatContactFormSubmitButton && !this.chatContactFormSubmittedSuccessfully" :messages="messages" />
  <div v-else>
    <div v-if="!hideFooter">
    <footer v-if="!hideReplyBox" class="footer">
      <ChatInputWrap
        :on-send-message="handleSendMessage"
        :on-send-attachment="handleSendAttachment"
        :disabled="replyBoxDisabled"
        :class="{ 'disabled-reply-box': replyBoxDisabled }"
      />
    </footer>
    <div v-else>
      <div class="flex items-center justify-between mb-4" v-if="!isOnline && !hiverConfig.show_contact_form">
        <div class="text-black-700">
          <div class="text-base leading-5 font-medium mb-1">
            {{
              outOfOfficeMessage
                ? outOfOfficeMessage
                : $t('TEAM_AVAILABILITY.OFFLINE')
            }}
          </div>
        </div>
      </div>
      <custom-button
        v-else
        class="font-medium send-message-mail-button"
        block
        :bg-color="widgetColor"
        :text-color="textColor"
        @click="startNewConversation"
      >
        {{ $t('START_NEW_CONVERSATION') }}
        <img :src="sendMessageArrowIcon" alt="Send Icon"/>
      </custom-button>
    </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getContrastingTextColor } from '@chatwoot/utils';
import CustomButton from 'shared/components/Button';
import ChatInputWrap from 'widget/components/ChatInputWrap.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { sendEmailTranscript } from 'widget/api/conversation';
import availabilityMixin from 'widget/mixins/availability';
import { allowedFileExtensions } from 'shared/helpers/FileHelper';
import { FILE_UPLOAD_ERROR } from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { MAXIMUM_FILE_UPLOAD_SIZE } from '../../shared/constants/messages';
import { sendExceptionToSentry } from 'shared/helpers/sentry';
import ChatContactFormSubmit from 'widget/components/ChatContactFormSubmit';
import configMixin from '../mixins/configMixin';
import sendMessageArrowIcon from '../assets/images/sendMessageArrowIcon.svg';
import { MESSAGE_TYPE } from 'widget/helpers/constants';

export default {
  components: {
    ChatInputWrap,
    ChatContactFormSubmit,
    CustomButton,
  },
  mixins: [availabilityMixin, configMixin],
  props: {
    msg: {
      type: String,
      default: '',
    },
    availableAgents: {
      type: Array,
    },
    messages: {
      type: Array
    }
  },
  data() {
    return {
      chatAvailabilityRequestInProgress: false,
      chatContactFormSubmittedSuccessfully: false,
      sendMessageArrowIcon,
      aiAgentTimeoutId: null,
      aiAgentTimedOut: false,
    };
  },
  computed: {
    ...mapGetters({
      conversationAttributes: 'conversationAttributes/getConversationParams',
      widgetColor: 'appConfig/getWidgetColor',
      getConversationSize: 'conversation/getConversationSize',
      currentUser: 'contacts/getCurrentUser',
      webChannelConfig: 'appConfig/getWebChannelConfig',
      outOfOfficeMessage: 'appConfig/getOutOfOfficeMessage',
      hiverConfig: 'hiverConfig/getHiverConfig',
      aiAgentMode: 'conversation/getAiAgentMode'
    }),
    textColor() {
      return getContrastingTextColor(this.widgetColor);
    },
    isOnline() {
      const { workingHoursEnabled } = this.channelConfig;
      const anyAgentOnline = this.availableAgents.length > 0;

      if (workingHoursEnabled) {
        return (anyAgentOnline && this.isInBetweenTheWorkingHours) || this.hasAConnectedAgentBot;
      }
      return anyAgentOnline || this.hasAConnectedAgentBot;
    },
    conversationStatus() {
      const { status } = this.conversationAttributes;
      return status;
    },
    hideFooter() {
      return this.conversationStatus === 'pending' && this.hasAConnectedAgentBot && !this.aiAgentMode;
    },
    hideReplyBox() {
      return this.conversationStatus === 'resolved';
    },
    lastMessage() {
      if (!Array.isArray(this.messages) || !this.messages.length) return {};
      const lastGroup = this.messages[this.messages.length - 1];
      if (!lastGroup || !Array.isArray(lastGroup.messages) || !lastGroup.messages.length) return {};
      return lastGroup.messages[lastGroup.messages.length - 1];
    },
    isLastMessageByUser() {
      const isMsgByUser =this.lastMessage?.message_type === MESSAGE_TYPE.INCOMING && this?.lastMessage?.sender?.type === 'contact';
      if(!isMsgByUser) {
        this.clearAiAgentTimeout();
      }
      return isMsgByUser;
    },
    replyBoxDisabled() {
      const shouldDisable = this.aiAgentMode && this.isLastMessageByUser && !this.aiAgentTimedOut;
      
      if (shouldDisable) {
        // Start timeout when disabling the reply box
        this.startAiAgentTimeout();
        this.$store.dispatch('conversation/toggleAgentTyping', {
          status: 'on'
        });
      } else {
        // Clear timeout and turn off typing when not disabled
        this.clearAiAgentTimeout();
        this.$store.dispatch('conversation/toggleAgentTyping', {
          status: 'off'
        });
      }
      
      return shouldDisable;
    },
    showEmailTranscriptButton() {
      return this.currentUser && this.currentUser.email;
    },
    showChatContactFormSubmitButton() {
        if (!Array.isArray(this.messages) || this.messages.length < 1 || this.conversationStatus === 'resolved') {
            return false
        }
        const { messages } = this.messages[0];
        return this.isLastMessageInSpecificContentType(messages, 'form') && this.hasAConnectedAgentBot;
    }
  },
  methods: {
    ...mapActions('conversation', [
      'sendMessage',
      'sendAttachment',
      'clearConversations',
    ]),
    ...mapActions('conversationAttributes', [
      'getAttributes',
      'clearConversationAttributes',
    ]),
    ...mapActions('agent', ['fetchAvailableAgents']),
    ...mapActions('hiverConfig', ['fetchHiverConfig']),
    async handleSendMessage(content) {
      const conversationSize = this.getConversationSize;
      await this.sendMessage({
        content,
      });
      // Update conversation attributes on new conversation
      if (conversationSize === 0) {
        this.getAttributes();
      }
    },
    handleSendAttachment(attachment) {
      if (!attachment || !attachment.file) {
        return;
      }

      const fileNameArr = attachment.file.name.split('.');
      const extnFromFileName = fileNameArr[fileNameArr.length - 1];
      if (!allowedFileExtensions.includes(`.${extnFromFileName.toLowerCase()}`)) {
        window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
          message: FILE_UPLOAD_ERROR,
        });
        return;
      }

      if (checkFileSizeLimit(attachment, MAXIMUM_FILE_UPLOAD_SIZE)) {
        const conversationSize = this.getConversationSize;
        this.sendAttachment({ attachment });
        // Update conversation attributes on new conversation
        if (conversationSize === 0) {
          this.getAttributes();
        }
      } else {
        window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
          message: this.$t('FILE_SIZE_LIMIT', {
            MAXIMUM_FILE_UPLOAD_SIZE,
          }),
        });
        return;
      }
    },
    async startNewConversation() {
      if (this.chatAvailabilityRequestInProgress) {
        return;
      }
      const { websiteToken } = this.webChannelConfig;
      await this.fetchHiverConfig(websiteToken)

      if (!this.hasAConnectedAgentBot) {
        this.chatAvailabilityRequestInProgress = true;
        await this.fetchAvailableAgents(websiteToken);
        this.chatAvailabilityRequestInProgress = false;
      }
      const { workingHoursEnabled } = this.channelConfig;
      const anyAgentOnline = this.availableAgents.length > 0 || this.hasAConnectedAgentBot;
      if ((!workingHoursEnabled && anyAgentOnline) || (workingHoursEnabled && this.isInBetweenTheWorkingHours && anyAgentOnline) || this.hasAConnectedAgentBot) {
        this.$store.dispatch('campaign/campaignMessageClicked', false);
        this.clearConversations();
        this.clearConversationAttributes();
        this.$emit('startNewConversation');
      } else if (this.hiverConfig.show_contact_form) {
        window.bus.$emit(BUS_EVENTS.SHOW_CONTACT_FORM);
      }
    },
    async sendTranscript() {
      const { email } = this.currentUser;
      if (email) {
        try {
          await sendEmailTranscript({
            email,
          });
          window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
            message: this.$t('EMAIL_TRANSCRIPT.SEND_EMAIL_SUCCESS'),
            type: 'success',
          });
        } catch (error) {
          window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
            message: this.$t('EMAIL_TRANSCRIPT.SEND_EMAIL_ERROR'),
          });
          sendExceptionToSentry(error);
        }
      }
    },
    isLastMessageInSpecificContentType(messages, type) {
    const lastMessage = messages[messages.length - 1];
    return lastMessage.content_type === type && !lastMessage.content_attributes?.submitted_values;
    },
    startAiAgentTimeout() {
      // Clear any existing timeout
      this.clearAiAgentTimeout();
      
      // Set new timeout for 45 seconds
      this.aiAgentTimeoutId = setTimeout(() => {
        this.aiAgentTimedOut = true;
        this.$store.dispatch('conversation/toggleAgentTyping', {
          status: 'off'
        });
      }, 45000); // 45 seconds
    },
    clearAiAgentTimeout() {
      if (this.aiAgentTimeoutId) {
        clearTimeout(this.aiAgentTimeoutId);
        this.aiAgentTimeoutId = null;
      }
      this.aiAgentTimedOut = false;
    }
  },
  mounted() {
    window.bus.$on(BUS_EVENTS.CHAT_CONTACT_FORM_SUBMIT_SUCCESS, () => {
        this.chatContactFormSubmittedSuccessfully = true;
    });
  },
  beforeDestroy() {
    // Clean up timeout when component is destroyed
    this.clearAiAgentTimeout();
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import '~widget/assets/scss/variables.scss';
@import '~widget/assets/scss/mixins.scss';

.footer {
  background: $color-white;
  box-sizing: border-box;
  padding: $space-small $space-slab;
  width: 100%;
  border-radius: 7px;
  @include shadow-big;
}

.branding {
  align-items: center;
  color: $color-body;
  display: flex;
  font-size: $font-size-default;
  justify-content: center;
  padding: $space-one;
  text-align: center;
  text-decoration: none;

  img {
    margin-right: $space-small;
    max-width: $space-two;
  }
}
.disabled-reply-box {
  pointer-events: none;
  opacity: 0.6;
  filter: grayscale(0.2);
}

</style>
